import { Injectable } from '@angular/core'

@Injectable({
  providedIn: 'root'
})
export class HeatmapService {
  private readonly ZERO_COLOR = 'rgba(200, 200, 200, 0.3)'
  private readonly GREEN_COLOR = [99, 190, 123, 1]
  private readonly RED_COLOR = [248, 105, 107, 1]

  getColorForNormalizedValue(normalizedValue: number, threshold: number, inverted = false): string {
    const lowColor = inverted ? this.GREEN_COLOR : this.RED_COLOR
    const highColor = inverted ? this.RED_COLOR : this.GREEN_COLOR

    if (normalizedValue < threshold) {
      const alpha = threshold > 0 ? 1 - (normalizedValue / threshold) * 0.9 : 0.1
      return `rgba(${lowColor[0]}, ${lowColor[1]}, ${lowColor[2]}, ${alpha.toFixed(2)})`
    } else {
      const remainingRange = 1 - threshold
      const alpha =
        remainingRange > 0 ? 0.1 + ((normalizedValue - threshold) / remainingRange) * 0.9 : 0.1
      return `rgba(${highColor[0]}, ${highColor[1]}, ${highColor[2]}, ${alpha.toFixed(2)})`
    }
  }

  /**
   * Gets color for a heatmap item, using gray for zeros
   */
  getColorForHeatmapItem(
    currentValue: number,
    min: number,
    max: number,
    threshold: number,
    inverted = false
  ): string {
    if (currentValue === 0) {
      return this.ZERO_COLOR
    }

    const range = max - min
    let normalizedValue = 0

    if (range !== 0) {
      normalizedValue = (currentValue - min) / range
    } else {
      normalizedValue = 0.5
    }

    return this.getColorForNormalizedValue(normalizedValue, threshold, inverted)
  }

  /**
   * Calculates min and max excluding all zeros
   */
  calculateStatsExcludingZeros(values: number[]): {
    min: number
    max: number
    filteredValues: number[]
  } {
    const filteredValues = values.filter((value) => value !== 0)

    if (filteredValues.length === 0) {
      // When all values are zeros, return default stats
      return { min: 0, max: 0, filteredValues: [] }
    }

    const min = Math.min(...filteredValues)
    const max = Math.max(...filteredValues)

    return { min, max, filteredValues }
  }
}
